import "./App.css";
import { Route, Routes, Navigate, useLocation, Outlet } from 'react-router-dom';
import Form from "./Form";
import CreateBriefComponent from "./features/CreateBrief/CreateBriefComponent";
import React, { useState, useEffect, useCallback } from "react";
import AlchemiqLogin from "./components/AlchemiqLogin";
import RequireAuth from "./components/RequireAuth";
import MobileNotSupported from "./components/MobileNotSupported";
import useIsMobile from "./hooks/useIsMobile";
import PublicStoryPage from "./pages/PublicStoryPage";
import PublicSuggestionsPage, { PublicSnapshotPage, PublicSharedArtifactPage, PublicSignalPage, PublicSignalStoryPage } from "./pages/PublicSuggestionsPage";
import FeedBuilderPage from "./features/FeedBuilder/FeedBuilderPage";
import UnsubscribePage from "./features/FeedBuilder/UnsubscribePage";
import CampaignRoute from "./features/Campaign/landing/CampaignRoute";
import LandingRoute from "./features/Campaign/landing/LandingRoute";
import TrialConfirmationPage from "./features/Trial/ConfirmationPage";
import TrialFeedPage from "./features/Trial/FeedPage";
import TrialStoryPage from "./features/Trial/StoryPage";
import TrialDraftPage from "./features/Trial/DraftPage";
import TrialRefinePage from "./features/Trial/RefinePage";
import TrialSavedPage from "./features/Trial/SavedPage";
import CampaignFaqPage from "./features/Campaign/CampaignFaqPage";
import CampaignPricingPage from "./features/Campaign/CampaignPricingPage";
import CampaignDemoPage from "./features/Campaign/CampaignDemoPage";
import SocialPostsList from "./features/SocialPosts/SocialPostsList";
import SocialPostEditor from "./features/SocialPosts/SocialPostEditorV2";
import BucketManager from "./features/BucketManager/BucketManager";
import PublisherBucketsNew from "./features/BucketManagerNew/PublisherBucketsNew";
import BucketStories from "./features/BucketManagerNew/BucketStories";
import InProduction from "./features/BucketManagerNew/InProduction";
import TwitterApiComponent from "./features/GetTweets";
import { Provider } from "react-redux";
import {store} from "./app/store";
import Ticker from "./features/Ticker/Ticker";
import Stories from "./features/Stories/Stories";
import StepIndicator from "./features/CreateBrief/StepIndicator";
import { ErrorProvider } from "./contexts/ErrorContext";
import { FrontendConfigProvider } from "./contexts/FrontendConfigContext";
import { OnboardingProvider, useOnboarding } from "./contexts/OnboardingContext";
import WelcomeModal from "./features/Onboarding/WelcomeModal";
import OnboardingPanel from "./features/Onboarding/OnboardingPanel";
import SavedBriefReadyNotification from './components/SavedBriefReadyNotification';
import { useAuth } from '@frontegg/react';
import { useCanEdit, useCanCreateBrief, useIsAdmin } from './hooks/useFrontegg';
import MyBriefsPage from './features/MyBriefs/MyBriefsPage';
import MyActivity from './pages/MyActivity';
import useActivityHeartbeat from './hooks/useActivityHeartbeat';

// Version check function to prevent cache issues
const checkForUpdates = () => {
    const currentVersion = process.env.REACT_APP_VERSION || '0.1.0';
    const storedVersion = localStorage.getItem('app_version');
    
    if (storedVersion && storedVersion !== currentVersion) {
        // New version detected, force reload without cache
        localStorage.setItem('app_version', currentVersion);
        window.location.reload(true);
    } else if (!storedVersion) {
        localStorage.setItem('app_version', currentVersion);
    }
};

function AppContent() {
    const { isAuthenticated } = useAuth();
    const location = useLocation();
    const isMobile = useIsMobile();
    const canEdit = useCanEdit();
    const canCreateBrief = useCanCreateBrief();
    const [currentStep, setCurrentStep] = useState(0);
    const [stepHandlers, setStepHandlers] = useState(null);

    useActivityHeartbeat();

    // Final onboarding step: once the user has started a draft generation and
    // lands on My Drafts, mark the "Visit draft page" step — the panel then
    // shows the success note and the Finish button.
    const { markStep: markOnboardingStep, state: onboardingState } = useOnboarding();
    const isOnMyBriefsRoute = location.pathname === '/my-drafts';
    useEffect(() => {
        if (!isOnMyBriefsRoute) return;
        if (!onboardingState?.steps?.generatedDraft || onboardingState?.steps?.sawDraft) return;
        markOnboardingStep('sawDraft');
    }, [isOnMyBriefsRoute, onboardingState, markOnboardingStep]);

    const handleStepHandlersReady = useCallback((handlers) => {
        setStepHandlers(handlers);
    }, []);
    
    // Check for updates only on initial mount (not on focus — that causes disruptive reloads)
    useEffect(() => {
        checkForUpdates();
    }, []);

    // Update currentStep when handlers change
    useEffect(() => {
        if (stepHandlers?.currentStep !== undefined) {
            setCurrentStep(stepHandlers.currentStep);
        }
    }, [stepHandlers?.currentStep]);

    // Block mobile users on all pages except login, Frontegg auth flows, and public story pages
    const isMobileAllowedRoute = location.pathname === '/login' || location.pathname.startsWith('/account/') || location.pathname.startsWith('/s/') || location.pathname.startsWith('/b/') || location.pathname.startsWith('/suggestions/') || location.pathname.startsWith('/feed-builder') || location.pathname.startsWith('/unsubscribe/') || location.pathname.startsWith('/c/') || location.pathname.startsWith('/lp/') || location.pathname.startsWith('/trial/') || location.pathname.startsWith('/signal-story/');
    if (isMobile && !isMobileAllowedRoute) {
        return <MobileNotSupported />;
    }
    
    // Show StepIndicator on all logged-in pages except login and public story pages
    // NAT-02 — the builder is an unauthenticated Alchemiq route, so it must skip
    // the authenticated chrome (step indicator, onboarding panel, nav) exactly as
    // the public story and suggestion pages do. Without this a visitor with no
    // session gets the signed-in shell wrapped around a public form.
    const isPublicRoute = location.pathname.startsWith('/s/') || location.pathname.startsWith('/b/') || location.pathname.startsWith('/suggestions/') || location.pathname.startsWith('/feed-builder') || location.pathname.startsWith('/unsubscribe/') || location.pathname.startsWith('/c/') || location.pathname.startsWith('/lp/') || location.pathname.startsWith('/trial/') || location.pathname.startsWith('/signal-story/');
    const isFixtureMode = new URLSearchParams(location.search).get('designFixture') === '1';
    const showStepIndicatorBase = (isAuthenticated || isFixtureMode) && location.pathname !== '/login' && !isPublicRoute;
    
    // Check if we're on a workflow page (home/search/story) vs a simple page (categories, etc.)
    const isWorkflowPage = location.pathname === '/' || location.pathname === '/news-search-with-claims' || location.pathname.startsWith('/story/');
    const isMyBriefsPage = location.pathname === '/my-drafts';

    return (
        <div className="app-container">
            {!isPublicRoute && (
            <div className="header-wrapper">
                {showStepIndicatorBase && isWorkflowPage && stepHandlers && (
                    <StepIndicator
                        currentStep={stepHandlers.currentStep || currentStep}
                        onStepClick={stepHandlers.onStepClick || ((step) => setCurrentStep(step))}
                        isNextStepEnable={stepHandlers.isNextStepEnable || (() => false)}
                        onSmartCopy={stepHandlers.onSmartCopy}
                        onPublishCms={stepHandlers.onPublishCms}
                        canCreateBrief={stepHandlers.canCreateBrief !== undefined ? stepHandlers.canCreateBrief : true}
                        sidebarOffset={stepHandlers.bucketSidebarOpen ? stepHandlers.bucketSidebarWidth || 280 : 0}
                        hasCopiedDraft={stepHandlers.hasCopiedDraft}
                        isPrimaryActionLoading={stepHandlers.isPrimaryActionLoading}
                    />
                )}
                {showStepIndicatorBase && isMyBriefsPage && (
                    <StepIndicator
                        currentStep={0}
                        onStepClick={() => {}}
                        isNextStepEnable={() => false}
                        canCreateBrief={canCreateBrief}
                        hideSteps
                    />
                )}
                {showStepIndicatorBase && canEdit && !isWorkflowPage && !isMyBriefsPage && !location.pathname.startsWith('/publisher-buckets') && location.pathname !== '/signals' && (
                    <StepIndicator 
                        currentStep={0}
                        onStepClick={() => {}}
                        isNextStepEnable={() => false}
                    />
                )}
                {showStepIndicatorBase && canEdit && (location.pathname.startsWith('/publisher-buckets') || location.pathname === '/signals') && (
                    <StepIndicator 
                        currentStep={0}
                        onStepClick={() => {}}
                        isNextStepEnable={() => false}
                        hideSteps
                    />
                )}
            </div>
            )}
            <div className="main-content">
                <SavedBriefReadyNotification enabled={Boolean(isAuthenticated)} />
                {isAuthenticated && (isWorkflowPage || isMyBriefsPage) && !isPublicRoute && (
                    <>
                        <WelcomeModal />
                        <OnboardingPanel />
                    </>
                )}
                <Routes>
                    <Route path="/login" element={<AlchemiqLogin />} />
                    <Route path="/s/:slug" element={<PublicStoryPage />} />
                    <Route path="/b/:bucketId/suggestions" element={<PublicSuggestionsPage />} />
                    <Route path="/suggestions/:publicToken" element={<PublicSnapshotPage />} />
                    <Route path="/shared/:publicToken" element={<PublicSharedArtifactPage />} />
                    <Route path="/signals/:publicToken" element={<PublicSignalPage />} />
                    <Route path="/signal-story/:token" element={<PublicSignalStoryPage />} />
                    {/* NAT-02 — the controlled Self-Service Feed Builder entry route.
                        Unauthenticated like the public suggestion pages above, and
                        deliberately inside the main app rather than a microsite. */}
                    <Route path="/feed-builder" element={<FeedBuilderPage />} />
                    {/* Opt-out for a delivered feed. Public and never gated by
                        the builder kill switch — turning off new signups must
                        not strand existing recipients. */}
                    <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
                    {/* PRD §1 — one campaign destination per audience, with no
                        selector and no link between them. The path segment is
                        what fixes the variant; attribution parameters are
                        recorded but never choose the copy. Unauthenticated, and
                        behind CAMPAIGN_EXPERIENCE_ENABLED. */}
                    <Route path="/c/:audience" element={<CampaignRoute />} />
                    {/* One clean ad URL per campaign. See landing/LandingRoute.jsx. */}
                    <Route path="/lp/:audience" element={<LandingRoute />} />
                    {/*
                      * The six trial pages. Public and token-scoped like /lp/
                      * and /c/: a trial subscriber has no account, so the
                      * edition token in the URL is the credential.
                      */}
                    <Route path="/trial/confirmation" element={<TrialConfirmationPage />} />
                    <Route path="/trial/feed/:token" element={<TrialFeedPage />} />
                    <Route path="/trial/feed/:token/refine" element={<TrialRefinePage />} />
                    <Route path="/trial/feed/:token/saved" element={<TrialSavedPage />} />
                    <Route path="/trial/feed/:token/story/:storyId" element={<TrialStoryPage />} />
                    <Route path="/trial/feed/:token/story/:storyId/draft" element={<TrialDraftPage />} />
                    {/* Q11/Q12 — the approved FAQ and Pricing pages. Nested under
                        the audience so the route still fixes the voice, and so the
                        header links from every campaign screen resolve. */}
                    <Route path="/c/:audience/faq" element={<CampaignFaqPage />} />
                    <Route path="/c/:audience/pricing" element={<CampaignPricingPage />} />
                    <Route path="/c/:audience/demo" element={<CampaignDemoPage />} />
                    {/* Main funnel — single CreateBriefComponent instance for all story routes.
                        Using a layout route prevents unmount/remount when navigating between
                        / and /story/:slug, which was causing blank feeds. */}
                    <Route element={<RequireAuth><CreateBriefComponent onStepChange={setCurrentStep} onStepHandlersReady={handleStepHandlersReady} /></RequireAuth>}>
                        <Route path="/" />
                        <Route path="/story/:slug" />
                        <Route path="/story/:slug/claims" />
                        <Route path="/story/:slug/draft" />
                        <Route path="/news-search-with-claims" />
                    </Route>
                    <Route path="/tweets-search" element={<TwitterApiComponent/>}/>
                    <Route path="/social-posts" element={<RequireAuth><SocialPostsList /></RequireAuth>} />
                    <Route path="/social-posts/:id" element={<RequireAuth><SocialPostEditor /></RequireAuth>} />
                    <Route path="/my-drafts" element={<RequireAuth><RequireBriefCreate><MyBriefsPage /></RequireBriefCreate></RequireAuth>} />
                    <Route path="/my-activity" element={<RequireAuth><RequireAdmin><MyActivity /></RequireAdmin></RequireAuth>} />
                    {/* Retired in favour of the Trending filter on the feed. A
                        Signal is a story, and the standalone page showed it as
                        a bare line of text: no article count, no preview, no
                        way to put it in a bucket. Redirected rather than
                        removed, so an existing bookmark still works. */}
                    <Route path="/signals" element={<Navigate to="/?trending=1" replace />} />
                    <Route path="/publisher-buckets" element={<RequireAuth><BucketManager /></RequireAuth>} />
                    <Route path="/publisher-buckets-new" element={<RequireAuth><PublisherBucketsNew /></RequireAuth>} />
                    <Route path="/publisher-buckets-new/bucket/:bucketId" element={<RequireAuth><BucketStories /></RequireAuth>} />
                    <Route path="/publisher-buckets-new/in-production" element={<RequireAuth><InProduction /></RequireAuth>} />
                    <Route path="/ticker" element={<Ticker/>}/>
                    {/* Email confirmation handled by Frontegg */}
                    {/* Add more routes as needed */}
                </Routes>
            </div>
            {!isPublicRoute && (
            <footer className="app-footer">
                <div className="footer-content">
                    <span className="footer-text">Alchemiq - News that drives growth © 2026</span>
                    <span className="footer-separator">•</span>
                    <a href="mailto:support@alchemiq.ai" className="footer-link">
                        Contact Support
                    </a>
                    <span className="footer-separator">•</span>
                    <div className="social-links">
                        <a href="https://www.linkedin.com/company/alchemiq-ai/" target="_blank" rel="noopener noreferrer" className="social-link" title="LinkedIn">
                            <img src="/linkedin.svg" alt="LinkedIn" />
                        </a>
                        <a href="https://www.youtube.com/@AlchemiqAI" target="_blank" rel="noopener noreferrer" className="social-link" title="YouTube">
                            <img src="/youtube.svg" alt="YouTube" />
                        </a>
                    </div>
                    <span className="footer-separator">•</span>
                    <span className="footer-version">v{process.env.REACT_APP_VERSION || '1.0.0'}</span>
                </div>
            </footer>
            )}
        </div>
    );
}

function RequireBriefCreate({ children }) {
    const canCreateBrief = useCanCreateBrief();
    if (!canCreateBrief) {
        return <Navigate to="/" replace />;
    }
    return children;
}

function RequireAdmin({ children }) {
    const isAdmin = useIsAdmin();
    if (!isAdmin) {
        return <Navigate to="/" replace />;
    }
    return children;
}

function App() {
    return (
        <Provider store={store}>
            <FrontendConfigProvider>
                <OnboardingProvider>
                    <ErrorProvider>
                        <AppContent />
                    </ErrorProvider>
                </OnboardingProvider>
            </FrontendConfigProvider>
        </Provider>
    )
}
export default App;
